import { Component } from '@angular/core';
import Highcharts from 'highcharts';
import { textOn } from './summary-boxes.component';
import { ChartPoint, ChartSeries, chartColors } from '../report-aggregation';
import { ChartBlockBase } from './chart-block-base';
import { Band, bandScale, bandsBestFirst, bandsOf } from './scale-bands';
import { CategoryMark, categoriesOf } from './row-categories';
import {
  BAR_BORDER_RADIUS,
  BAR_GAP_PX,
  BAR_LABEL_GUTTER_PX,
  BAR_THICKNESS_PX,
  barLabelColumn,
  barPointWidth,
  categoryLabelPlacement,
  CHART_SPACING,
  endLabelFor,
  CHART_V_SPACING,
  COLUMN_PLOT_COMPACT_PX,
  COLUMN_PLOT_PX,
  axisMaxFor,
  columnValueAxis,
  categoryLabelStyle,
  endLabelDataLabels,
  endLabelMargins,
  categoryAxis,
  categoryLabelFormatter,
  clampedCategories,
  hiddenPercentAxis,
  isHorizontal,
  labelFitFor,
  legendHeightFor,
  lineHeightFor,
  valueFormat,
  legendRowsFor,
  seriesLegend,
} from './chart-layout';

/**
 * The bottom band, and an answer no band claims at all.
 *
 * Two greys, a step apart, for the reason the dial's ring needs two: the bottom band is a reading
 * and an unclaimed answer is not one, so they cannot be the same colour or a trailing "Not sure"
 * reads as more detractors.
 */
const INERT_BAND = 'var(--ecr-grey, #c3ced3)';
const UNBANDED_FILL = 'var(--ecr-line, #dbe4e8)';

/** How many colour slots the app-wide stylesheet defines a `.highcharts-color-N` rule for. */
const HIGHCHARTS_SLOTS = 10;
/** The slot after the bands, which `paintSlots` gives the unbanded fill. */
const UNBANDED_SLOT = -1;

@Component({
  selector: 'app-bar-chart-block',
  standalone: false,
  template: `
    @if (chartOptions) {
      <!-- Rebuilt, not updated, when the chart is turned: Highcharts decides whether a chart is
         on its side before it changes the series' type, so an update from bars to columns is
         judged against the bars still there and the chart stays sideways. -->
      @for (turn of [orientation]; track turn) {
        <highcharts-chart [Highcharts]="highcharts" [oneToOne]="true" [options]="chartOptions"
                          style="width: 100%; display: block;"></highcharts-chart>
      }
    }

    <!-- The bands named under the columns they cover. Drawn here rather than as a second Highcharts
         axis: what this needs is a rule of a given width with two lines of text under it, which is a
         flex row, where a category axis would have to be tricked into grouping its own ticks.
         Proportional because a category slot is a fixed share of the plot, so a band of three
         answers is three slots wide wherever the chart is resized to. -->
    <!-- Named where the bars are themed. A battery sorted by size scatters a theme down the list,
         which is the whole reason for naming it. -->
    @if (categoryKey.length) {
      <div class="cat-key">
        <span class="cat-mark cat-key-name">Category key</span>
        @for (mark of categoryKey; track mark.label) {
          <span class="cat-mark">
            <i class="cat-dot" [style.background]="mark.colour" aria-hidden="true"></i>{{ mark.label }}
          </span>
        }
      </div>
    }

    @if (bandAxis.length) {
      <div class="band-axis"
           [style.padding-left.px]="sidePad" [style.padding-right.px]="sidePad">
        @for (band of bandAxis; track $index) {
          <div class="band-span" [style.flexGrow]="band.span">
            <span class="band-rule" [style.background-color]="band.colour"></span>
            <span class="band-label" [style.color]="band.colour">{{ band.label }}</span>
          </div>
        }
      </div>
    }
  `,
  // Inline by default, which would make the width measured in ChartBlockBase 0.
  styles: [`
    :host { display: block; }
    /* Inset by the chart's own side spacing so the strip lines up with the plot rather than with
       the card -- bound rather than written here, because a styles block has to resolve statically
       at compile time and CHART_SPACING is read at runtime. The gap between two bands is a margin on
       the rule, not on the box: the boxes have to stay exactly proportional to the columns above
       them or the rules drift off their bars. */
    .band-axis {
      display: flex;
    }
    .cat-key {
      column-gap: 1rem;
      display: flex;
      flex-wrap: wrap;
      margin-top: 0.4rem;
      row-gap: 0.2rem;
    }
    .cat-key-name {
      font-weight: 700;
    }
    .cat-mark {
      align-items: center;
      color: var(--ecr-mut, #5a6b73);
      display: flex;
      font-size: var(--ecr-font-xs, 11px);
    }
    /* Square: this is a key, naming a colour. The ranked driver keeps a round mark for the dot
       that sits beside a row, which is a different job. */
    .cat-dot {
      border-radius: 2px;
      flex: none;
      height: 8px;
      margin-right: 5px;
      width: 8px;
    }
    .band-span {
      flex-basis: 0;
      flex-shrink: 1;
      min-width: 0;
      text-align: center;
    }
    .band-rule {
      border-radius: 1px;
      display: block;
      height: 2px;
      margin: 0 4px 6px;
    }
    .band-label {
      display: block;
      font-size: var(--ecr-font-sm, 12px);
      font-weight: 700;
      line-height: 1.2;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `]
})
export class BarChartBlockComponent extends ChartBlockBase {
  highcharts = Highcharts;
  chartOptions: Highcharts.Options | null = null;
  orientation: 'horizontal' | 'vertical' = 'horizontal';
  /** What the chart insets its plot by, which the band strip under it has to match. */
  readonly sidePad = CHART_SPACING[3];

  /** The themes drawn across the bars, for the key under the chart. Empty where none are set. */
  categoryKey: CategoryMark[] = [];

  /**
   * The bands as a strip under the columns: how many of them each covers, and what to call it.
   *
   * No range here, unlike the dial's list. The answers are printed under the bars a few pixels
   * above, so naming them again a second time says nothing the reader cannot already see.
   */
  bandAxis: { label: string; span: number; colour: string }[] = [];

  /**
   * A fill per answer, where the block groups its scale into bands -- the 1-10 distribution drawn so
   * the promoter end, the middle and the detractor end read as three groups rather than as one
   * colour repeated ten times.
   *
   * Empty, so every bar falls back to its series colour, unless all three hold:
   *
   * - **one question.** With several, colour already means which question, and the legend depends
   *   on that.
   * - **bands that fit.** `bandsOf` returns null rather than truncating where they do not. The
   *   series keeps every answer for exactly this reason, including ones nobody picked.
   *
   * The bands are walked over the answers **sorted back into asked order**, not over the order they
   * happen to be drawn in, and the fills are then matched to the drawn points by answer. Banding the
   * drawn order directly meant a chart carrying a `sortDescending` from before its bands were added
   * silently coloured nothing at all -- the right answer, arrived at by the wrong route and
   * indistinguishable from the feature not working.
   *
   * Coloured **best-first**, exactly as the dial colours the ring it draws from the same bands: the
   * top band takes the palette's first role and the bottom one the inert grey, so a score and the
   * distribution under it agree rather than each running the palette in its own direction.
   *
   * Walked over the **whole** answer list in `known`, then matched onto the answers actually drawn:
   * hiding the ones nobody picked is display only, and a band counted over the shorter list would
   * slide onto the wrong answers.
   *
   * Returns the **colour slot** for each drawn answer, not its colour. A point's `color` is written
   * as a `fill` attribute, and the app-wide `.highcharts-color-N { fill: var(--highcharts-color-N) }`
   * beats an attribute, so colouring the points directly left every bar in slot 0's colour. Dropping
   * the series `colorIndex` does not help either -- Highcharts allocates one. So each point takes the
   * slot for its band and `paintSlots` redefines those slots on this block's own element, where they
   * override the ten the deck publishes on the block host.
   */
  private applyBands(known: ChartSeries[], drawn: ChartSeries[], horizontal: boolean): number[] {
    this.bandAxis = [];
    this.paintSlots([]);
    if (this.block.reportType !== 'bar-chart') return [];
    const spec = this.block.reportExtraData.bands;
    if (!spec?.length || known.length !== 1) return [];

    const points = drawn[0].points;
    const scale = [...bandScale(known[0].points)].sort((a, b) => a.optionIndex - b.optionIndex);
    const bands = bandsOf(scale, spec);
    if (!bands) return [];

    const topFirst = (this.block.reportExtraData.topBand ?? 'first') === 'first';
    const ranked = bandsBestFirst(bands, topFirst);
    const fills = chartColors(this.palette, Math.max(1, ranked.length));
    const colourOf = (band: Band) => {
      const rank = ranked.indexOf(band);
      return rank === ranked.length - 1 && ranked.length > 1 ? INERT_BAND : fills[rank];
    };
    const colours = bands.map(colourOf);
    this.paintSlots(colours);

    // One flat colour per band, the same one its rule and its name carry underneath. The ladder's
    // within-band tint was tried and dropped here: on a ladder a shade per rung distinguishes cells
    // that carry no other mark, but these bars already have their answer under them and their
    // percentage above, so varying the fill only weakened the tie between a bar and its band.
    const byLabel = new Map<string, number>();
    bands.forEach((band, index) => band.points.forEach(point => byLabel.set(point.label, index)));
    const slots = points.map(point => byLabel.get(point.label) ?? UNBANDED_SLOT);

    // Only under an upright chart. Turned on its side the bands run down the rows, and a strip
    // along the foot would name them against the wrong axis.
    //
    // Measured in **drawn columns**, by running along the same slots the bars took, rather than in
    // band sizes. Those are not the same number: an answer no band claims is still a column, so a
    // strip divided by band size spread three bands across the width of four and every rule sat off
    // its bars. Run-length encoding the slots cannot drift, because it is the bars.
    if (!horizontal) {
      slots.forEach((slot, at) => {
        const run = this.bandAxis[this.bandAxis.length - 1];
        if (at && slot === slots[at - 1]) {
          run.span++;
          return;
        }
        const band = slot === UNBANDED_SLOT ? null : bands[slot];
        this.bandAxis.push({
          label: band?.label ?? '',
          span: 1,
          colour: band ? colours[slot] : '',
        });
      });
    }

    return slots;
  }

  /**
   * Redefines this block's chart colour slots, one per band, with the unbanded slot after them.
   *
   * Written onto the component's own element rather than the block host's: custom properties
   * inherit, so the nearer declaration wins and only this chart repaints. Cleared first on every
   * build, or a chart that loses its bands keeps painting in them.
   */
  private paintSlots(colours: string[]) {
    const style = this.host.nativeElement.style;
    for (let slot = 0; slot < HIGHCHARTS_SLOTS; slot++) {
      const colour = slot === colours.length && colours.length ? UNBANDED_FILL : colours[slot];
      if (colour) style.setProperty(`--highcharts-color-${slot}`, colour);
      else style.removeProperty(`--highcharts-color-${slot}`);
    }
  }

  /**
   * The series as drawn: without the answers nobody picked, where the block hides them. An answer
   * goes only when it is empty in every series, since a battery's axis is shared. Not on a numeric
   * battery, whose bars are means and whose zero is a score rather than an absence.
   */
  private withoutEmptyAnswers(known: ChartSeries[]): ChartSeries[] {
    if (this.block.reportType !== 'bar-chart' || !this.block.reportExtraData.hideEmptyAnswers) return known;
    const picked = new Set(known.flatMap(entry => entry.points
      .filter(point => entry.numeric || point.value > 0 || point.optionIndex < 0)
      .map(point => point.label)));
    return known.map(entry => ({ ...entry, points: entry.points.filter(point => picked.has(point.label)) }));
  }

  protected build() {
    if (this.block.reportType !== 'bar-chart') return;
    const horizontal = isHorizontal(this.block.reportType, this.block.reportExtraData.orientation);
    this.orientation = horizontal ? 'horizontal' : 'vertical';
    // The first series only if it resolved: one unknown question at the front would otherwise take
    // the category axis down with it and render an empty chart beside questions that do have data.
    const known = this.series.filter(s => s.known);
    const drawn = this.withoutEmptyAnswers(known);
    const categories = drawn.find(s => s.points.length)?.points.map(p => p.label) ?? [];
    // A numeric battery's bars are means, so nothing here may print a "%" on them.
    const format = valueFormat(
      drawn.some(s => s.numeric), this.block.reportExtraData.valueUnit);

    const axisMax = axisMaxFor(this.series);
    const rows = Math.max(categories.length, this.alignCount ?? 0);

    const roles = chartColors(this.palette, Math.max(1, drawn.length));
    // Picked against each series' own fill, since an inside label is printed on the bar.
    const insideInk = roles.map(role => textOn(role, 'var(--ecr-ink, #1b2b33)', '#ffffff'));
    // One colour slot per answer where the block bands its scale, otherwise empty and every bar
    // takes its series colour as before.
    const banded = this.applyBands(known, drawn, horizontal);

    // Themes colour the *rows* of a battery, where bands colour the answers of one question -- so a
    // chart doing one is never doing the other, and this only reads when there is a bar per series.
    // `themes`, not `categories`: this chart already calls its axis labels that, and the two are
    // different things -- one is the answers along an axis, the other a grouping of the series.
    // Keyed by position in variableKeys, not by the series name: a battery's rows can share one
    // question's wording, and a name that repeats cannot tell two rows apart. `drawn` is filtered,
    // so the index comes from the unfiltered series list.
    const chartedKeys = this.block.reportType === 'bar-chart'
      ? this.block.reportExtraData.variableKeys ?? []
      : [];
    const keyOf = new Map(this.series.map((s, at) => [s, chartedKeys[at] ?? s.name]));
    // Several keys arriving as one series are a bar per key rather than a series per key: the items
    // of a battery, or the options of a multi-punch question, folded together by columnSeries with
    // each point's optionIndex naming its key. Themed by series there, every bar took the first
    // key's category and the key named only that one.
    const barPerKey = this.series.length === 1 && chartedKeys.length > 1;
    const keyOfPoint = (p: ChartPoint) => chartedKeys[p.optionIndex];
    const themes = categoriesOf(
      banded.length ? undefined : this.block.reportExtraData.categories,
      barPerKey
        ? (drawn[0]?.points ?? []).filter(p => p.optionIndex >= 0).map(keyOfPoint)
        : known.map(s => keyOf.get(s)!),
      this.palette);
    this.categoryKey = themes.legend;
    // A bar no category claims goes inert once any category is drawn, as on the ranked driver: left
    // on the series colour it would be the palette's first role, which is also the first category's.
    const unclaimed = themes.legend.length ? INERT_BAND : undefined;
    const pointColour = (p: ChartPoint) => barPerKey && p.optionIndex >= 0
      ? themes.colourOf(keyOfPoint(p)) ?? unclaimed
      : undefined;
    // The series' own theme where a series is one key; a bar-per-key series is themed by its points.
    const seriesTheme = (s: ChartSeries) => barPerKey ? undefined : themes.colourOf(keyOf.get(s)!);
    // An inside figure is picked against the bar it is printed on, which is the point's own colour
    // once bars are themed one by one. The inert grey is a CSS variable textOn cannot read, and is
    // light, so it always takes the dark ink.
    const inkOn = (p: ChartPoint, i: number) => {
      const fill = pointColour(p);
      if (!fill) return insideInk[i];
      return fill === INERT_BAND
        ? 'var(--ecr-ink, #1b2b33)'
        : textOn(fill, 'var(--ecr-ink, #1b2b33)', '#ffffff');
    };
    const bandCount = this.bandAxis.filter(band => band.label).length;
    const slotAt = (at: number) => (banded[at] === UNBANDED_SLOT ? bandCount : banded[at]);
    const labelSize = this.labelSize();
    const fontFamily = this.fontFamily();
    const lineHeight = lineHeightFor(labelSize);
    const labelColumn = barLabelColumn(this.plotWidth());
    const fit = labelFitFor({
      plotWidth: this.plotWidth(),
      slots: rows,
      categories,
      labelSize,
      horizontal,
      horizontalWidth: labelColumn - BAR_LABEL_GUTTER_PX,
      fontFamily,
      // One line, always. A row is the bar's own height plus its gap, so a label long enough to
      // wrap would have to push the rows apart -- and a chart whose spacing depends on how long
      // the answers happen to be does not read as one chart. Too long is truncated instead; the
      // full text stays the category, so the tooltip still has it.
      ...(horizontal ? { maxLines: 1 } : {}),
    });

    const labelText = clampedCategories(categories, fit, labelSize, fontFamily);

    // A category slot holds one bar per charted question. Taller slots are NOT the lever here: a bar
    // is (1 - 2*groupPadding)(1 - 2*pointPadding)/n of its slot, so with the single-series padding
    // below the inked width stays a constant third of the slot however many questions there are --
    // a taller slot just grows the gaps with it. The padding does the work (see plotOptions), and
    // the slot only has to hold the bars the padding produces.
    const bars = Math.max(1, drawn.length);

    // One series is a plain bar chart and 0.28 is the airy look it was tuned for. Several series
    // share a slot and pointPadding applies to each one's share of it, so the same value that reads
    // as breathing room at n=1 costs more than half of every bar at n=3. The grouped values are the
    // ones categorical-distribution already uses, for exactly this reason.
    //
    // A single series takes the prototype's flat thickness. Several share a slot and cannot: a
    // fixed width there would either overflow the slot or leave the group floating in it, so they
    // keep dividing it by padding and only the corner radius is shared.
    const padding = bars > 1
      ? { pointPadding: 0.02, groupPadding: 0.18 }
      : {
        pointPadding: 0.28,
        groupPadding: 0.12,
      };

    const hideLabels = !!this.block.reportExtraData.hideLabels;

    // Horizontal reclaims the space the hidden % axis used to need at the bottom. Vertical keeps its
    // category labels down there either way, and needs room for the lines they wrap to. The legend
    // is counted because Highcharts takes its height out of the plot: unreserved, it was silently
    // thinning every bar on a multi-question chart.
    const legendRows = legendRowsFor(
      drawn.map(s => s.name), this.plotWidth(), this.fontFamily());
    const legendCost = legendHeightFor(legendRows);
    // Exactly what is spent outside the plot, so what is left is exactly the rows. This was a flat
    // 84 with a fudge factor for the legend; against a real 24 of spacing that handed 60px back to
    // the plot, which came out as extra air between the bars rather than as a margin.
    const chrome = horizontal
      ? CHART_V_SPACING + legendCost
      : (hideLabels ? 60 : 110 + (fit.lines - 1) * lineHeight) + legendCost;

    // Flat: every bar on the chart takes the same thickness and every gap is the same 7px, so a
    // chart of three answers and a chart of thirty have identical rows. Nothing about the labels
    // enters this any more -- they are cut to one line above rather than allowed to set the pitch.
    // Only a horizontal chart stacks its bars down the slot; on a vertical one the series divide a
    // category's *width*, so counting them into the height just makes a tall chart of thin columns.
    const rowHeight = horizontal ? bars * BAR_THICKNESS_PX + BAR_GAP_PX : 34;

    this.chartOptions = this.deckMotion({
      chart: {
        type: horizontal ? 'bar' : 'column',
        // No floor on a horizontal chart. Padding the height out to a minimum does not make the
        // bars bigger -- thickness is a flat pointWidth now -- so every spare pixel lands in the
        // gaps between rows instead. A four-row chart was being stretched to 280px and drawing its
        // 15px bars with 40px of air between them. A column chart takes a fixed plot instead: its
        // answers run across, so they set the columns' width and not the chart's height.
        height: horizontal
          ? rows * rowHeight + chrome
          : (this.compact ? COLUMN_PLOT_COMPACT_PX : COLUMN_PLOT_PX) + chrome,
        spacing: CHART_SPACING,
        ...endLabelMargins(horizontal),
        // Pins the label column so the bars all start at the same x, on this chart and every other
        // one in the deck. Without it Highcharts measures the longest label and the split drifts.
        // Set either way, never left out: see categoryLabelPlacement. Absent, the label column
        // a horizontal chart reserves stays reserved once the chart is turned upright.
        marginLeft: horizontal && !hideLabels ? labelColumn : undefined,
        style: { fontFamily: 'inherit' },
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: drawn.length > 1 ? seriesLegend() : { enabled: false },
      xAxis: categoryAxis({
        categories,
        labels: {
          step: 1,
          formatter: categoryLabelFormatter(labelText, true),
          // Ranged left, from the card's edge, like the prototype's `.row .lab`. Highcharts anchors
          // an inverted chart's category labels at the axis and ranges them right from there, so
          // the offset pulls the anchor back across the whole label column to its start. The 8px
          // the column keeps in hand (see `fit`) is what stops the longest answer touching its bar.
          ...categoryLabelPlacement(horizontal, labelColumn),
          style: {
            ...categoryLabelStyle(fit, labelSize),
            textAlign: horizontal ? 'left' : 'center',
          },
        },
      }, !hideLabels),
      yAxis: horizontal ? hiddenPercentAxis(axisMax) : columnValueAxis(!!this.compact),
      tooltip: {
        // A mean has no respondent count of its own to show in brackets -- the series' n is the
        // same for every item -- so a numeric chart shows the figure alone.
        pointFormat: drawn.some(s => s.numeric)
          ? `<b>${format.pointFormat}</b>`
          : '<b>{point.y:.0f}%</b> ({point.custom.count})',
      },
      plotOptions: {
        // A bar begins exactly on the plot's left edge, so the tip of its rounded start corner
        // falls on the clip boundary and gets shaved -- that end then reads as squarer than the
        // other. clip:false removes the boundary; nothing can overflow it anyway, since the axis
        // maximum is derived from the data.
        series: { borderWidth: 0, clip: false },
        bar: { grouping: true, borderRadius: BAR_BORDER_RADIUS, ...padding, ...(bars === 1 ? barPointWidth(rowHeight) : {}) },
        column: { grouping: true, ...padding, borderRadius: BAR_BORDER_RADIUS },
      },
      series: [
        // The track: the rest of the row to the axis maximum, so a bar reads against what it did
        // not fill -- the prototype's `.row .bar` background. Drawn first so it sits behind, and
        // `grouping: false` keeps it centred on the category rather than taking a slot of its own.
        //
        // Single series only: grouped bars divide their slot and sit off-centre within it, so one
        // centred track behind them would line up with none of them.
        ...(horizontal && bars === 1 ? [{
          type: 'bar',
          name: '',
          data: categories.map(() => axisMax),
          color: 'var(--ecr-bg, #eef3f5)',
          pointWidth: BAR_THICKNESS_PX,
          borderRadius: BAR_BORDER_RADIUS,
          grouping: false,
          enableMouseTracking: false,
          showInLegend: false,
          animation: false,
          // Off the colour cycle and out of the tooltip: this is stationery, not a reading.
          colorIndex: undefined,
        }] : []),
        ...drawn.map((s, i) => ({
        type: horizontal ? 'bar' : 'column',
        name: s.name,
        colorIndex: banded.length || seriesTheme(s) ? undefined : i,
        color: banded.length ? undefined : seriesTheme(s) ?? roles[i],
        dataLabels: endLabelDataLabels(11, format.pointFormat),
        data: s.points.map((p, at) => ({
          y: p.percent,
          custom: { count: p.value },
          className: p.optionIndex < 0 ? 'chart-track' : undefined,
          // The slot for this answer's band, which paintSlots has redefined. Per point rather than
          // per series, because the band changes partway along the series.
          ...(banded.length ? { colorIndex: slotAt(at) } : {}),
          // Its own key's category, where each bar is a key of its own (see barPerKey).
          ...(!banded.length && pointColour(p) ? { color: pointColour(p) } : {}),
          // Horizontal puts the figure inside the bar once it is long enough to hold it and
          // outside while it is not -- see INSIDE_LABEL_MIN_PERCENT -- per point, because which side
          // a figure belongs on is a property of that bar's length rather than of the question. A
          // column has no such choice: `inside` there is measured against the column's *height*, so
          // a tall bar swallowed its figure while a short one kept it, and the row of percentages
          // stopped reading as a row. Always above the column instead.
          dataLabels: horizontal ? endLabelFor(p.percent, inkOn(p, i)) : { inside: false },
        })),
      })),
      ] as Highcharts.SeriesOptionsType[],
    });
  }
}
